import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ApiCalls from '../../common/ApiCalls';
import './styles.scss';

export default function GeoZoneSelect({
    value,
    onChange,
    label = 'Geo Zone',
    placeholder = 'Select a geo zone...',
    className = '',
    disabled = false,
    onlyWithPolygons = true,
    includeEmptyOption = true
}) {
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(true);

    useEffect(() => {
        let mounted = true;
        const api = ApiCalls();

        (async () => {
            try {
                setLoading(true);
                setErr(null);
                const res = await api.getAllGeoZones();

                const list = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);

                const filtered = (onlyWithPolygons
                    ? list.filter(z => Array.isArray(z.geo_zones) && z.geo_zones.length > 0)
                    : list
                );

                filtered.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

                if (mounted) setZones(filtered);
            }catch (e) {
                if (mounted) setErr(e?.message || 'Failed to load geo zones');
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => { mounted = false; };
    }, [onlyWithPolygons]);

    const options = useMemo(() => {
        return zones.map(z => {
            const polygonCount = Array.isArray(z.geo_zones) ? z.geo_zones.length : 0;
            const firstWkt = polygonCount > 0 ? z.geo_zones[0]?.wkt || null : null;
            return {
                id: z.id,
                name: z.name,
                polygonCount,
                firstWkt
            };
        });
    }, [zones]);

    const handleChange = (e) => {
        const id = e.target.value || null;
        if (!id) {
            onChange?.(null);
            return;
        }
        const picked = options.find(o => o.id === id) || null;
        onChange?.(picked);
    };

    const isDisabled = disabled || loading || !!err;

    return(
        <div className={`geo-zone-select ${className}`} data-testid="geo-zone-zelect">
            <div className="geo-zone-select__control">
                <select
                id="geo-zone-select__select"
                className="geo-zone-select__select"
                value={value || ''}
                onChange={handleChange}
                disabled={isDisabled}
                aria-invalid={!!err}
                >
                    {includeEmptyOption && (
                        <option value="">{loading ? 'Loading...' : placeholder}</option>
                    )}
                    {!loading && options.map(opt => (
                        <option key={opt.id} value={opt.id}>
                            {opt.name}{opt.polygonCount > 1 ? ` (${opt.polygonCount})` : ''}
                        </option>
                    ))}
                </select>

                {loading && <span className="geo-zone-select__hint">Loading zones...</span>}
                {!loading && !err && options.length === 0 && (
                    <span className="geo-zones-select__hint">No geo zones found</span>
                )}
                {err && <span className="geo-zone-select__error">{err}</span>}
            </div>
        </div>
    );
}

GeoZoneSelect.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    onlyWithPolygons: PropTypes.bool,
    includeEmptyOption: PropTypes.bool
};